import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  X,
  Plus,
  Check,
  Loader2,
  PackageX,
  Receipt as ReceiptIcon,
  AlertCircle,
  Calendar,
  User as UserIcon,
  MonitorSmartphone,
  ImageOff,
  Printer,
  PackageOpen,
  Undo2,
  Copy,
  Play,
  Ban,
  MessageSquare,
  Truck,
  CheckCircle2,
  Clock,
  MapPin,
} from 'lucide-react'
import {
  useTransaction,
  useReceipt,
  useCreateRefund,
  useVoidReceipt,
  useSetKioskStatus,
  useCancelOrder,
  useRefundOnlinePayment,
  PAYMENT_TYPES,
  RECEIPT_STATUS,
  LOCKED_RECEIPT_STATUSES,
  useDeliveryStatusHistory,
  useSetDeliveryStatus,
  DELIVERY_STATUS,
  DELIVERY_STATUS_LABEL,
  type DeliveryStatus,
  type TransactionItem,
  type TransactionPayment,
  type ReceiptItem,
  type Receipt,
  type Transaction,
} from '@/api/receipts'
import { CustomerMessageDialog } from './CustomerMessageDialog'
import { RefundOptionsDialog } from './RefundOptionsDialog'
import { usePrintReceipt } from '@/api/payments'
import { usePOSConfig } from '@/api/config'
import { isRetailCustomer } from '@/api/customers'
import { productKeys } from '@/api/products'
import { v2, v2api } from '@/api/v2'
import { fetchProductsForRefundItems, seedRefundPreview } from '@/lib/refundBootstrap'
import { useNavigate } from '@tanstack/react-router'
import { useFinishEcomSale, useRecallReceipt, useLoadPaidOnlineOrder, useIsMobile } from '@/hooks'
import { fulfillmentType } from '@/lib/fulfillment'
import { usePOSStore } from '@/stores/posStore'
import { useSessionStore } from '@/stores/sessionStore'
import { canBuyAgain, describeBuyAgainRefusal, isForeignStoreReceipt } from '@/lib/customerOrders'
import { useUIStore } from '@/stores/uiStore'
import { formatCurrency, toNumber, extractTaxes } from '@/lib/utils'
import { withCdnImageWidth } from '@/lib/cdnImage'
import type { Customer, Product } from '@/types'
import { approvedByNames, approvedByText } from '@/lib/discountLimit'
import { PaymentMethodLabel } from '@/components/CardBrandMark'

// ---------- Helpers ----------

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const d = new Date(value)
  if (!Number.isNaN(d.getTime())) return d
  const m = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (m) return new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]))
  return null
}

function formatDateTime(value: string | null | undefined): string {
  const d = parseDate(value)
  if (!d) return '—'
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// We blend two backend records: the canonical "receipt" (items/totals/
// payments are well-structured) and the richer "transaction" (brand, image,
// register/clerk metadata at the line + header level). Helpers below accept
// either shape via a loose index signature so we don't care which we got.
type AnyItem = (TransactionItem | ReceiptItem) & Record<string, unknown>

function readNumber(item: AnyItem, ...keys: string[]): number | null {
  for (const k of keys) {
    const v = item[k]
    if (v == null) continue
    const n = typeof v === 'number' ? v : parseFloat(String(v))
    if (Number.isFinite(n)) return n
  }
  return null
}

function getItemProductId(item: AnyItem): number | null {
  const product = item.product as unknown
  if (product && typeof product === 'object' && 'id' in product) {
    const pid = (product as { id?: unknown }).id
    if (pid != null && Number.isFinite(Number(pid))) return Number(pid)
  }
  if (typeof product === 'number') return product
  return readNumber(item, 'product_id', 'product__id', 'productId')
}

function getItemQty(item: AnyItem): number {
  const q = readNumber(item, 'qty', 'quantity', 'amount')
  return q ?? 1
}

function getItemUnitPrice(item: AnyItem): number {
  return readNumber(item, 'price', 'unit_price') ?? 0
}

function getItemSubtotal(item: AnyItem): number {
  const sub = readNumber(item, 'subtotal', 'total', 'line_total')
  if (sub != null) return sub
  return getItemUnitPrice(item) * getItemQty(item)
}

function getItemDiscount(item: AnyItem): number {
  return readNumber(item, 'discount_amount', 'discount') ?? 0
}

function getItemName(item: AnyItem): string {
  const product = item.product as unknown
  if (product && typeof product === 'object') {
    const p = product as { name?: string; description?: string }
    if (p.name) return p.name
    if (p.description) return p.description
  }
  const pid = getItemProductId(item)
  return (
    (item.product_name as string | undefined) ||
    (item.display_name as string | undefined) ||
    (item.description as string | undefined) ||
    (pid != null ? `Item #${pid}` : 'Item')
  )
}

function getItemSku(item: AnyItem): string | null {
  return (item.sku as string | undefined) || (item.cspcid as string | undefined) || null
}

function getItemBrand(item: AnyItem): string | null {
  const brandName = item.brand_name as string | undefined
  if (brandName) return brandName
  const brand = item.brand
  if (typeof brand === 'string') return brand
  return null
}

function getItemSize(item: AnyItem): string | null {
  return (
    (item.size as string | undefined) ||
    (item.unit as string | undefined) ||
    (item.unit_name as string | undefined) ||
    null
  )
}

function getItemImage(item: AnyItem, product?: Product): string | null {
  return (
    (item.image_url as string | undefined) ||
    (item.thumbnail_url as string | undefined) ||
    (item.image as string | undefined) ||
    product?.image_url ||
    product?.thumbnail_url ||
    product?.image ||
    null
  )
}

function getItemLot(item: AnyItem): string | null {
  // BE uses both names depending on era — `lot_number` on legacy
  // receipts, `batch_number` on the v2 batches contract. Whichever
  // the order detail returns, surface it as the LOT badge.
  return (
    (item.lot_number as string | undefined)
    || (item.batch_number as string | undefined)
    || null
  )
}

function getItemReturnedQty(item: AnyItem): number {
  // Only the v2 receipt payload carries it (post split-refund BE);
  // missing → 0 so old backends render exactly as before.
  return readNumber(item, 'returned_qty') ?? 0
}

function getItemKey(item: AnyItem, idx: number): string {
  if (item.id != null) return `id-${item.id}`
  const pid = getItemProductId(item)
  if (pid != null) return `pid-${pid}-${idx}`
  return `row-${idx}`
}

// Pulls the linked receipt id off the transaction record. Different builds
// of the legacy backend put it under different keys.
// Resolve a receipt line's product at THIS store the way the catalog
// does, so the cart gets this store's live retail price and on-hand.
// Exact id, no fallbacks. The previous version searched the SKU as free
// text and took `list[0]` when nothing matched the id (a different
// product into the cart), then fell back to the unscoped
// /objects/products/:id master row (net price, no per-store stock,
// which the stock check read as "unknown" and allowed). Receipts from
// other stores make both cases common: a product this store never
// carried resolves to nothing here, and that is the right answer.
async function lookupCatalogProduct(id: number): Promise<Product | null> {
  try {
    const data = await v2api.get<{ products?: Product[] } | Product[]>(
      v2('/products/search'),
      { id, per_page: 5 },
    )
    const list: Product[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.products)
        ? data!.products!
        : []
    return list.find((p) => Number(p.id) === id) ?? null
  } catch {
    return null
  }
}

function getReceiptIdFromTransaction(tx: Transaction | undefined): number | null {
  if (!tx) return null
  for (const k of ['receipt', 'receipt_id', 'receipt_obj'] as const) {
    const v = tx[k]
    if (v == null) continue
    const n = typeof v === 'number' ? v : parseFloat(String(v))
    if (Number.isFinite(n)) return n
  }
  return null
}

// Merge transaction line metadata onto the receipt items so the UI can show
// brand/image/sku enrichment that the receipts payload omits.
function mergeItems(
  receiptItems: ReceiptItem[] | undefined,
  txItems: TransactionItem[] | undefined,
): AnyItem[] {
  const rItems = (receiptItems ?? []) as AnyItem[]
  if (rItems.length === 0) return (txItems ?? []) as AnyItem[]
  if (!txItems || txItems.length === 0) return rItems

  // Index transaction items by product id for cheap merging. Falls back to
  // sequential pairing if product ids aren't usable.
  const txByPid = new Map<number, TransactionItem>()
  for (const t of txItems) {
    const pid = getItemProductId(t as AnyItem)
    if (pid != null && !txByPid.has(pid)) txByPid.set(pid, t)
  }
  return rItems.map((r, idx) => {
    const pid = getItemProductId(r)
    const match = (pid != null && txByPid.get(pid)) || txItems[idx]
    if (!match) return r
    // Receipt fields win on numerics (qty/price/totals); transaction fills
    // descriptive metadata that's missing on the receipt side.
    const merged: AnyItem = { ...(match as AnyItem), ...r }
    return merged
  })
}

// Special products have negative ids on the BE: the gift card (-111),
// Delivery (-123), CannaDrop (-321), Paid In/Out, rewards. They are money,
// not goods (the BE pins their on-hand at 0), so they must never render the
// "Out of stock" badge the way a real catalog item would.
function isSpecialProductId(productId: number | null | undefined): boolean {
  return productId != null && productId < 0
}

function getStockState(
  product: Product | undefined,
  hideOutOfStock: boolean,
): 'unknown' | 'in' | 'out' {
  if (!product) return 'unknown'
  // Per-store merchandising flags (see Product.is_inactive). Only mean
  // "can't be sold" at a store with frontend.hide_out_of_stock on --
  // same condition the till applies, so the badge matches what the Add
  // button will actually do (canBuyAgain gets the same flag).
  if (hideOutOfStock && (product.is_inactive === true || product.out_of_stock === true)) {
    return 'out'
  }
  const onHand =
    product.on_hand ??
    product.quantity_available ??
    product.quantity ??
    null
  if (onHand == null) return 'unknown'
  return onHand > 0 ? 'in' : 'out'
}

// Moneris Checkout card-brand codes -> display labels (subset we see in
// practice; unknown codes fall back to "Online Card (<code>)").
const ONLINE_CARD_BRANDS: Record<string, string> = {
  V: 'Online · Visa',
  M: 'Online · Mastercard',
  AX: 'Online · AMEX',
  NO: 'Online · Discover',
  D: 'Online · Debit',
}

const PAYMENT_TYPE_LABELS: Record<number, string> = {
  [PAYMENT_TYPES.CASH]: 'Cash',
  [PAYMENT_TYPES.EXCHANGE]: 'Exchange',
  [PAYMENT_TYPES.MASTER_CARD]: 'Mastercard',
  [PAYMENT_TYPES.POINTS]: 'Points',
  [PAYMENT_TYPES.DEBIT]: 'Debit',
  [PAYMENT_TYPES.VISA]: 'Visa',
  [PAYMENT_TYPES.AMEX]: 'AMEX',
  [PAYMENT_TYPES.CHANGE]: 'Change',
  [PAYMENT_TYPES.E_TRANSFER]: 'e-Transfer',
  [PAYMENT_TYPES.AR]: 'On account',
  [PAYMENT_TYPES.GIFT_CARD]: 'Gift card',
  [PAYMENT_TYPES.PAY_FROM_ACC]: 'Account balance',
  [PAYMENT_TYPES.CHEQUE]: 'Cheque',
  [PAYMENT_TYPES.ROUNDING_DIFF]: 'Rounding',
  [PAYMENT_TYPES.OTHER_CC]: 'Other card',
  [PAYMENT_TYPES.SPRINGBIG_POINTS]: 'SpringBig points',
}

function getPaymentLabel(p: TransactionPayment): string {
  if (p.payment_type_name) return p.payment_type_name
  if (p.payment_method) return p.payment_method
  if (typeof p.payment_type === 'number' && PAYMENT_TYPE_LABELS[p.payment_type]) {
    return PAYMENT_TYPE_LABELS[p.payment_type]
  }
  if (p.card_type) return p.card_type
  return 'Payment'
}

// ---------- Per-item Add state ----------

// `unavailable` is TERMINAL and expected: the line can't be sold at this
// register (not carried, no stock, no price). It carries the reason so
// the row can say which, and both `isOut` and `remainingAddable` treat
// it as a dead end, so the Add button turns into the "Unavailable" chip.
// `error` is reserved for a THROWN exception (network, bad payload) --
// it stays retryable, which is why a refusal must never be filed as one.
type AddState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'added' }
  | { kind: 'error'; message: string }
  | { kind: 'unavailable'; message?: string }

export interface OrderDetailModalProps {
  // Two entry points:
  //   - `transactionId`: legacy callers (e.g. an explicit transactions feed)
  //     hand us a transaction id; we fetch /transactions/:id for the rich
  //     item metadata and follow it to the linked receipt.
  //   - `receiptId`: most callers (Orders tab via receipts/search,
  //     CustomerModal via the activity log which is also receipts/search)
  //     hand us a receipt id directly; skip the transaction round-trip.
  // IDs are not interchangeable — passing a receipt id as `transactionId`
  // hits /transactions/:id and 404s.
  transactionId?: number
  receiptId?: number
  receiptNumber?: string
  onClose: () => void
  // Read-only mode: hide the per-line + "Add all" buttons. Used when opening
  // an order purely for review (e.g. from the Orders tab).
  hideAddActions?: boolean
  // Optional click handler for the customer name in the header. When set,
  // the customer line becomes a button that hands a seed Customer back to
  // the parent — which typically closes this modal and opens CustomerModal.
  onOpenCustomer?: (customer: Customer) => void
  // Optional jump-to-receipt handler for the source-receipt link on a
  // refund. Parent typically swaps its `openOrder` state to the source
  // so the modal re-opens against the original sale.
  onOpenReceipt?: (args: { receiptId?: number; receiptNumber?: string }) => void
  // Originating store, when the caller already knows it (CustomerModal's
  // cross-store Orders tab). Lets the modal go view-only before the
  // receipt payload arrives; otherwise `store_id` on the payload decides.
  originStoreId?: number | null
  originStoreName?: string | null
}

export function OrderDetailModal({
  transactionId,
  receiptId: directReceiptId,
  receiptNumber,
  onClose,
  hideAddActions = false,
  onOpenCustomer,
  onOpenReceipt,
  originStoreId,
  originStoreName,
}: OrderDetailModalProps) {
  const queryClient = useQueryClient()
  const { addToCart, setActiveTab } = usePOSStore()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const showToast = useUIStore((s) => s.showToast)
  // The store's merchandising setting. `frontend.hide_out_of_stock` is
  // the only condition under which the till refuses an inactive /
  // flagged-out product, so it decides both what "buy again" allows and
  // whether a line renders the "Out of stock" badge. Unloaded config
  // (undefined) reads as off -- same as an unset SysConfig.
  const { data: posConfig } = usePOSConfig()
  const hideOutOfStock = posConfig?.posHideOutOfStock === true
  const printReceipt = usePrintReceipt()
  // Separate instance so the prep-slip print doesn't spin the Print button.
  const printPrepSlip = usePrintReceipt()
  const createRefund = useCreateRefund()
  const voidReceipt = useVoidReceipt()
  const setKioskStatus = useSetKioskStatus()
  const cancelOrder = useCancelOrder()
  const showConfirm = useUIStore((s) => s.showConfirm)
  const { finish: finishEcomSale, isPending: isFinishing } = useFinishEcomSale()
  const { load: loadPaidOnline, isPending: isLoadingPaidOnline } = useLoadPaidOnlineOrder()
  const refundOnline = useRefundOnlinePayment()
  const { recall: recallReceipt, isPending: isRecalling } = useRecallReceipt()

  // Only chase the transaction record when we don't already have a receipt id.
  const skipTransaction = directReceiptId != null
  const { data: transaction, isLoading: txLoading, error: txError } = useTransaction(
    skipTransaction ? null : (transactionId ?? null)
  )

  // Pull the canonical receipt for items/totals/payments. The legacy POS
  // hits both endpoints in parallel for the same reason — each carries a
  // strict subset of the data we need to render this modal.
  const receiptId = directReceiptId ?? getReceiptIdFromTransaction(transaction)
  const { data: receipt, isLoading: rcLoading, error: rcError } = useReceipt(
    receiptId ?? null,
    { includeItems: true },
  )

  const isLoading = (!skipTransaction && txLoading) || (receiptId != null && rcLoading)
  // Only surface the transaction error if we have nothing else to render.
  const error = !skipTransaction && txError && !receipt
    ? txError
    : skipTransaction && rcError
      ? rcError
      : !transaction && rcError
        ? rcError
        : null

  // Items: receipt is source-of-truth for numerics; transaction supplies
  // brand/image/sku enrichment when present.
  const items: AnyItem[] = useMemo(
    () => mergeItems(receipt?.items, transaction?.items),
    [receipt?.items, transaction?.items],
  )

  // Header metadata can come from either record; receipt wins on flags
  // like `on_hold` because it's the live record. We only read string/flag
  // fields off `merged`, so the loose record type is enough.
  const merged: Partial<Omit<Receipt, 'items'>> & Partial<Omit<Transaction, 'items'>> = {
    ...((transaction ?? {}) as Partial<Omit<Transaction, 'items'>>),
    ...((receipt ?? {}) as Partial<Omit<Receipt, 'items'>>),
  }

  // A receipt rung at another store is VIEW ONLY on this register. Every
  // write route on the portal is store-scoped and answers 409
  // `foreign_store_receipt` for it; hiding the controls here just says so
  // before the cashier taps. "Buy again" stays: it resolves each line
  // against this store's catalogue (see addLine).
  const sessionStoreId = useSessionStore((s) => s.selectedStore?.id ?? null)
  // Cache key for the STORE-SCOPED catalog row (retail price, on_hand,
  // is_inactive / out_of_stock). Deliberately not `productKeys.detail(id)`:
  // other producers fill that key with the master row from
  // /objects/products/{id} (no per-store stock, net price), and with a
  // 5-minute staleTime whichever wrote first wins -- a master row here
  // would make canBuyAgain read "no stock" on a stocked product, and a
  // catalog row there would put this store's price into other views.
  // (refundBootstrap writes this same store-scoped key for the same
  // reason; anything still on the plain detail key is a master row.)
  const catalogKey = useCallback(
    (id: number) => [...productKeys.detail(id), 'store-catalog', sessionStoreId] as const,
    [sessionStoreId],
  )
  // `merged`, not `receipt`: the transaction payload carries store_id too
  // (Transaction extends Omit<Receipt, 'items'>), and on the
  // transactionId entry point the receipt may never resolve at all.
  const receiptStoreId =
    originStoreId ?? (typeof merged.store_id === 'number' ? merged.store_id : null)
  const isForeignStore = isForeignStoreReceipt(receiptStoreId, sessionStoreId)
  const foreignStoreName =
    originStoreName
    ?? merged.store_name
    ?? (receiptStoreId != null ? `Store ${receiptStoreId}` : null)
  // Every mutating control needs BOTH conditions: the receipt belongs to
  // this store, AND its payload has landed. The load half matters when
  // the caller has no `originStoreId` (the Orders tab, a source-receipt
  // jump) -- until store_id arrives, `isForeignStore` is false by
  // design, so Refund / Cancel / Print would render live for ~200ms on a
  // receipt that turns out to be another store's.
  const canMutateHere = !isForeignStore && !isLoading

  const [addStates, setAddStates] = useState<Record<string, AddState>>({})
  const [products, setProducts] = useState<Record<number, Product | null>>({})
  const [bulkAdding, setBulkAdding] = useState(false)
  const [orderNumberCopied, setOrderNumberCopied] = useState(false)

  // `products` holds THIS store's catalog rows, so a store switch under
  // an open modal (the session can change from the CFD/session
  // broadcast) invalidates every entry -- including the `null` misses,
  // which would otherwise keep a line the new store does carry pinned at
  // "Unavailable". Drop the map and the refusals derived from it; the
  // prefetch below then refills it on the new store's catalogKey.
  // `added` states survive: they record lines actually placed in the cart.
  //
  // Adjusted DURING RENDER (React's documented "reset state when a value
  // changes" pattern), not from an effect. The prefetch effect skips ids
  // already in `products`, and effects run in declaration order — a reset
  // queued from an effect would land after that sweep had already decided
  // there was nothing to fetch, and its deps wouldn't change on the
  // follow-up render, so the map would stay empty for good.
  const [catalogStore, setCatalogStore] = useState(sessionStoreId)
  if (catalogStore !== sessionStoreId) {
    setCatalogStore(sessionStoreId)
    setProducts({})
    setAddStates((prev) => {
      const next: Record<string, AddState> = {}
      for (const [key, state] of Object.entries(prev)) {
        if (state.kind === 'added') next[key] = state
      }
      return next
    })
  }

  // Lazily prefetch product data so we can show stock state + reuse on add.
  // Goes through the catalog endpoint so the price matches what the
  // products grid shows. Runs once per items list change; failed fetches
  // store `null` so we render an "unavailable" state without retrying.
  useEffect(() => {
    if (items.length === 0) return
    let cancelled = false
    const seen = new Set<number>()
    const lookups: number[] = []
    for (const item of items) {
      const id = getItemProductId(item)
      if (id == null || seen.has(id) || products[id] !== undefined) continue
      seen.add(id)
      lookups.push(id)
    }
    if (lookups.length === 0) return

    Promise.all(
      lookups.map(async (id) => {
        try {
          const product = await queryClient.fetchQuery({
            queryKey: catalogKey(id),
            queryFn: () => lookupCatalogProduct(id),
            staleTime: 1000 * 60 * 5,
          })
          return [id, product] as const
        } catch {
          return [id, null] as const
        }
      }),
    ).then((results) => {
      if (cancelled) return
      setProducts((prev) => {
        const next = { ...prev }
        for (const [id, product] of results) next[id] = product
        return next
      })
    })

    return () => {
      cancelled = true
    }
    // `products` stays out of the deps on purpose -- the effect writes it,
    // so including it would re-run the whole sweep on every result batch.
    // `catalogKey` is in: it is memoised on sessionStoreId, and leaving it
    // out meant a store switch kept prefetching into the old store's key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, queryClient, catalogKey])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const customerName =
    merged.customer?.display_name ||
    [merged.customer?.first_name, merged.customer?.last_name].filter(Boolean).join(' ') ||
    null
  const customerId = merged.customer?.id ?? merged.customer_id ?? null
  // Hand the parent a minimal Customer seed; CustomerModal refetches via
  // `useCustomer(seed.id)` so missing fields fill in on its first render.
  //
  // Retail Customer (the walk-in / anonymous default) is excluded — it's a
  // system pseudo-record with no real profile, so opening the customer
  // modal for it would render empty stub data. Falls through to the plain
  // <span> branch below so the name still displays, just without the
  // clickable affordance.
  const handleCustomerClick =
    onOpenCustomer && customerId != null && !isRetailCustomer(customerId)
      ? () =>
          onOpenCustomer({
            id: customerId,
            display_name: customerName ?? undefined,
            first_name: merged.customer?.first_name,
            last_name: merged.customer?.last_name,
          })
      : null
  const headerNumber =
    merged.number || receiptNumber || `#${transactionId ?? directReceiptId ?? ''}`

  const setItemState = (key: string, state: AddState) =>
    setAddStates((prev) => ({ ...prev, [key]: state }))

  // Add a single line. Resolves true if added, false otherwise.
  async function addLine(item: AnyItem, key: string): Promise<boolean> {
    const productId = getItemProductId(item)
    if (productId == null) {
      setItemState(key, { kind: 'unavailable' })
      return false
    }
    setItemState(key, { kind: 'loading' })
    let product = products[productId]
    try {
      if (product === undefined || product === null) {
        product = await queryClient.fetchQuery({
          queryKey: catalogKey(productId),
          queryFn: () => lookupCatalogProduct(productId),
          staleTime: 1000 * 60 * 5,
        })
        setProducts((prev) => ({ ...prev, [productId]: product ?? null }))
      }

      // Fail closed: not carried here / no stock / no price always
      // refuse; inactive / flagged-out only where the store hides them,
      // which is the only case the till refuses them too. The portal's
      // add_item gates are the backstop; this is the same answer before
      // the cart is touched.
      //
      // A refusal is `unavailable`, NOT `error`: it is terminal and
      // expected, so the row shows the reason and the Add button retires
      // (an `error` stays tappable, for a genuine exception below).
      const verdict = canBuyAgain(product, { hideOutOfStock })
      if (!verdict.ok || !product) {
        setItemState(key, {
          kind: 'unavailable',
          message: describeBuyAgainRefusal(verdict) ?? 'Not available at this store',
        })
        return false
      }

      const qty = Math.max(1, Math.round(getItemQty(item)))
      // Another store's lot number means nothing here -- let this store
      // pick FIFO. (The portal re-validates a supplied lot against this
      // store anyway; not sending one avoids picking a shared producer
      // lot out of FIFO order.)
      addToCart(product, qty, isForeignStore ? null : (getItemLot(item) ?? null))
      setItemState(key, { kind: 'added' })
      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not add'
      setItemState(key, { kind: 'error', message })
      return false
    }
  }

  async function handleAddAll() {
    if (items.length === 0) return
    setBulkAdding(true)
    let added = 0
    let skipped = 0
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      const key = getItemKey(item, i)
      const current = addStates[key]
      if (current?.kind === 'added') continue
      const ok = await addLine(item, key)
      if (ok) added++
      else skipped++
    }
    setBulkAdding(false)
    if (added > 0) {
      showToast({
        type: 'success',
        title: `Added ${added} item${added === 1 ? '' : 's'} to cart`,
        message: skipped > 0 ? `${skipped} skipped (unavailable)` : undefined,
      })
    } else if (skipped > 0) {
      showToast({
        type: 'warning',
        title: 'No items added',
        message: 'All items are unavailable or already added.',
      })
    }
  }

  // The receipts endpoint sometimes returns `payments: []` (empty array)
  // even when the journal transaction has them — `??` doesn't fall through
  // on an empty array, so we have to length-check explicitly.
  // Both payment payloads ship `amount` tip-INCLUSIVE (the charged
  // amount) as of the 2026-07-09 BE change -- the v2 receipt always
  // did (jamount), and the transaction endpoints were aligned when the
  // back-office viewer moved to showing the charged amount. No
  // normalization: adding tip_amount here would double-count.
  const jtPayments: TransactionPayment[] =
    (receipt?.payments && receipt.payments.length > 0
      ? receipt.payments
      : transaction?.payments) ?? []

  // Online (ecom) card payments book no Payment JT — the money lives at
  // the gateway and the BE ships a redacted `online_payment` block
  // instead. Synthesize one display row so the modal's PAYMENT section
  // renders it like any tender (brand · last4 · amount, plus a
  // "refunded" chip once reversed).
  const onlinePayment = receipt?.online_payment
  const payments: TransactionPayment[] =
    jtPayments.length === 0 && onlinePayment
      ? [{
          id: -1,
          payment_type: -1,
          payment_method:
            ONLINE_CARD_BRANDS[onlinePayment.card_type ?? '']
              ?? (onlinePayment.card_type
                ? `Online Card (${onlinePayment.card_type})`
                : 'Online Card'),
          last4: onlinePayment.card_last_four ?? undefined,
          amount: onlinePayment.amount ?? 0,
          status: onlinePayment.refunded ? 'refunded' : 'success',
        } as unknown as TransactionPayment]
      : jtPayments

  // Integrated-terminal tips ride on the payment rows (`tip_amount`)
  // and are NOT inside receipt.total; cart/kiosk tips ARE inside
  // receipt.total (and their payment rows carry tip_amount too, since
  // pay() stamps JT.cost for both models). `terminalTipComponent`
  // below isolates the part missing from the receipt-sourced total.
  const tipsFromPayments = payments.reduce(
    (sum, p) => sum + (toNumber(p.tip_amount) || 0),
    0,
  )

  // The legacy `transactions/:id` endpoint returns totals as a label/value
  // tuple array (`total_lines: [['Subtotal', N], ['HST', N], ['Total', N]]`)
  // rather than a `totals` sub-object — see waio-portal/.../frontend.py:7334
  // (`obj['total_lines'] = [...]`). We synthesize a normalized record so
  // `readTotal` can use it as one more fallback source. Tax lines are
  // labelled by their tax name (HST/GST/PST/etc.), so anything that isn't
  // one of the well-known buckets is summed into `taxes`.
  const txTotalLines = useMemo<Record<string, number>>(() => {
    const lines = (transaction as { total_lines?: unknown } | undefined)?.total_lines
    if (!Array.isArray(lines)) return {}
    const out: Record<string, number> = {}
    let taxSum = 0
    let hasTax = false
    for (const line of lines) {
      if (!Array.isArray(line) || line.length < 2) continue
      const [labelRaw, val] = line as [unknown, unknown]
      if (typeof labelRaw !== 'string') continue
      const num = toNumber(val)
      if (!Number.isFinite(num)) continue
      const key = labelRaw.toLowerCase().trim()
      if (key === 'subtotal') out.subtotal = num
      else if (key === 'total') out.total = num
      else if (key === 'discount') out.discount = num
      else if (key === 'deposit') out.deposit = num
      else if (key.includes('recycling')) out.deposit = (out.deposit ?? 0) + num
      else if (key === 'tips' || key === 'tip') {
        // Legacy transaction payloads: this line is the cart+terminal
        // COMBINED sum and `Total` already includes it. Captured only
        // as a last-resort display fallback -- never into the taxes
        // bucket, never re-added to Total, and never used as the cart
        // component in tip math (that would double-count terminal).
        out.tips = (out.tips ?? 0) + num
      } else if (num !== 0) {
        taxSum += num
        hasTax = true
      }
    }
    if (hasTax) out.taxes = taxSum
    return out
  }, [transaction])

  // Reads totals from a `totals` sub-object, falling back to the same keys
  // at the record root, and finally to the parsed `total_lines` table for
  // legacy transactions that don't expose a `totals` object at all.
  const readTotal = (...keys: string[]): number | null => {
    const sources: Array<Record<string, unknown> | undefined> = [
      receipt?.totals as Record<string, unknown> | undefined,
      receipt as unknown as Record<string, unknown> | undefined,
      transaction?.totals as Record<string, unknown> | undefined,
      transaction as unknown as Record<string, unknown> | undefined,
      txTotalLines,
    ]
    for (const src of sources) {
      if (!src) continue
      for (const k of keys) {
        const v = src[k]
        if (v == null) continue
        const n = toNumber(v)
        if (Number.isFinite(n)) return n
      }
    }
    return null
  }

  const subtotal = readTotal('subtotal', 'sub_total', 'amount')
  const tax = readTotal('taxes', 'tax', 'tax_amount', 'tax1')
  const discount = readTotal('discount_amount', 'discount')
  // Manager approvals of over-limit discounts (receipt and line scopes).
  const discountApprovedBy = approvedByText(approvedByNames(receipt?.discount_overrides))
  const deposit = readTotal('deposit_amount', 'deposit')
  const total = readTotal('total', 'grand_total', 'amount_total')
  const amountPaid = readTotal('amount_paid', 'paid', 'tendered')
  const changeDue = readTotal('change_due', 'change')

  // The two tip models are ADDITIVE and must never be conflated:
  //  * terminal tips = payment rows' tip_amount (terminal-only by the
  //    BE predicate on BOTH payload types) — NOT inside `total`.
  //  * cart tip = `tips_amount` (receipt root post-BE-contract, or the
  //    transaction root) — INSIDE `total`.
  // Deliberately NOT sourced from the legacy total_lines 'Tips' tuple:
  // that line is the cart+terminal combined sum and would double-count
  // the terminal portion; it serves only as the display fallback when
  // neither model resolves (very old transaction payloads).
  const cartTip = readTotal('tips_amount') ?? 0
  const combinedTips = cartTip + tipsFromPayments
  const tipsDisplay =
    combinedTips > 0.005 ? combinedTips : (txTotalLines.tips ?? 0)
  // Only a receipt-sourced `total` is missing the terminal component
  // (cart tips are inside it either way; transaction-sourced totals
  // already include both tip models via the BE grand_total).
  const rawReceiptTotal = (
    receipt?.totals as Record<string, unknown> | undefined
  )?.total
  const totalIsReceiptSourced =
    rawReceiptTotal != null
    && Number.isFinite(
      typeof rawReceiptTotal === 'string'
        ? parseFloat(rawReceiptTotal)
        : Number(rawReceiptTotal),
    )
  const terminalTipComponent = totalIsReceiptSourced ? tipsFromPayments : 0

  const remainingAddable = useMemo(
    () =>
      items.filter((item, idx) => {
        const key = getItemKey(item, idx)
        const s = addStates[key]
        if (s?.kind === 'added') return false
        if (s?.kind === 'unavailable') return false
        if (getItemProductId(item) == null) return false
        const productId = getItemProductId(item)
        // null = looked up, not carried at this store -> not addable.
        if (productId != null && products[productId] === null) return false
        const stock = getStockState(
          productId != null ? (products[productId] ?? undefined) : undefined,
          hideOutOfStock,
        )
        return stock !== 'out'
      }).length,
    [items, addStates, products, hideOutOfStock]
  )

  const allAdded =
    items.length > 0 &&
    items.every((i, idx) => addStates[getItemKey(i, idx)]?.kind === 'added')

  const handleCopyOrderNumber = useCallback(async () => {
    const value = String(headerNumber ?? '').replace(/^#/, '')
    if (!value) return
    try {
      await navigator.clipboard.writeText(value)
      setOrderNumberCopied(true)
      setTimeout(() => setOrderNumberCopied(false), 1500)
    } catch {
      // Clipboard blocked (insecure context, denied permission, etc.) —
      // fail silently rather than surface a noisy error for a copy action.
    }
  }, [headerNumber])

  const handlePrint = useCallback(() => {
    if (receiptId == null) return
    // `is_copy` flags this as a reprint so the legacy backend stamps the
    // ticket as "DUPLICATE" rather than re-running the original print
    // pipeline.
    printReceipt.mutate(
      { receiptId, isCopy: true },
      {
        onSuccess: () => showToast({ type: 'success', title: 'Receipt sent to printer' }),
        onError: (err) =>
          showToast({
            type: 'error',
            title: 'Print failed',
            message: err instanceof Error ? err.message : undefined,
          }),
      },
    )
  }, [printReceipt, receiptId, showToast])

  // Ecom (online, not in-store kiosk) order detection. Mirrors the v1
  // search filter (`online=true && instorekiosk=false`). Only ecom orders
  // get the "Prepare & send to printer" button — in-store kiosk orders
  // were already prepped by the customer at the kiosk terminal.
  const isEcomOrder = merged.online === true && !merged.instorekiosk
  // Kiosk pipeline state. Mirrors KioskReceiptStatus on the BE
  // (project/frontend/models/pos.py): 0=New, 1=Preparing, 2=Ready,
  // 3=Completed. Gates which workflow button surfaces in the footer.
  const kioskStatus = typeof merged.kiosk_status === 'number' ? merged.kiosk_status : null
  // For ecom orders, show the fulfillment type (Pickup / Delivery) in the
  // header instead of a redundant "Online" badge — matches the order cards.
  const ecomFulfillment = isEcomOrder
    ? fulfillmentType({
        is_delivery: merged.is_delivery ?? undefined,
        delivery_status:
          typeof merged.delivery_status === 'string' ? merged.delivery_status : null,
      })
    : null
  const canPrepare = isEcomOrder && kioskStatus === 0 && canMutateHere
  // Ready / Remind buttons are hidden for in-store kiosk orders (the
  // customer is standing at the kiosk; SMS is meaningless). Mirrors the
  // `!instorekiosk` guard in v1's pos.js:243,250.
  const canMarkReady = isEcomOrder && kioskStatus === 1 && canMutateHere
  const canRemind = isEcomOrder && kioskStatus === 2 && canMutateHere

  // CustomerMessageDialog mount state. Two trigger paths feed the same
  // dialog component — `mode` decides whether sending also flips
  // kiosk_status from 1 -> 2 ('ready') or leaves it at 2 ('remind').
  const [messageDialog, setMessageDialog] = useState<
    { open: false } | { open: true; mode: 'ready' | 'remind' }
  >({ open: false })

  const handlePrepare = useCallback(async () => {
    if (receiptId == null) return
    // Status first — a missing/offline printer must not block the order
    // from moving to Preparing. The prep slip is best-effort; on failure
    // the cashier can reprint from the Print button.
    try {
      await setKioskStatus.mutateAsync({ receiptId, kioskStatus: 1 })
    } catch (err) {
      showToast({
        type: 'error',
        title: 'Could not prepare order',
        message: err instanceof Error ? err.message : undefined,
      })
      return
    }
    try {
      await printPrepSlip.mutateAsync({ receiptId, isCopy: false })
      showToast({
        type: 'success',
        title: 'Marked as Preparing',
        message: 'Prep slip sent to printer.',
      })
    } catch {
      showToast({
        type: 'warning',
        title: 'Marked as Preparing',
        message: "Prep slip didn't print — use Print to retry.",
      })
    }
    onClose()
  }, [printPrepSlip, setKioskStatus, receiptId, showToast, onClose])

  // Send-message side effect. The dialog handles the BE call + toast;
  // here we only run the post-send transition. Mirrors v1's split where
  // `send_message().then(...)` chains the kiosk_status PUT after the
  // SMS/email succeeds (pos.js:354 vs pos.js:372 — remind is a no-op).
  const handleAfterReadySent = useCallback(async () => {
    if (receiptId == null) return
    try {
      await setKioskStatus.mutateAsync({ receiptId, kioskStatus: 2 })
    } catch (err) {
      showToast({
        type: 'error',
        title: 'Message sent, but status not updated',
        message: err instanceof Error ? err.message : undefined,
      })
    }
  }, [setKioskStatus, receiptId, showToast])

  // The dialog's "Mark ready without sending a message": same transition,
  // no SMS/email. For a customer with no reachable contact, or an SMS the
  // provider keeps rejecting -- the order must not be stuck at Preparing
  // because a text could not go out.
  const handleMarkReadyWithoutMessage = useCallback(async () => {
    if (receiptId == null) return false
    try {
      await setKioskStatus.mutateAsync({ receiptId, kioskStatus: 2 })
      showToast({
        type: 'success',
        title: 'Marked as Ready',
        message: 'No message was sent to the customer.',
      })
      return true
    } catch (err) {
      showToast({
        type: 'error',
        title: 'Could not mark order ready',
        message: err instanceof Error ? err.message : undefined,
      })
      return false
    }
  }, [setKioskStatus, receiptId, showToast])

  // "Complete" — pull the ecom order into the cart BOUND to its receipt
  // (loadReceiptIntoCart sets currentReceiptId) so the pay-time /cart/commit
  // overwrites THIS receipt in place instead of minting a duplicate.
  //
  // Ecom orders are created at Hold (waio-portal frontend.py customer_kiosk /
  // greenery paths). "Complete" runs finishEcomSale, which flips the receipt
  // Hold->New. If that sale is then abandoned (cashier clears the cart and
  // re-rings) the order is stranded at New — and gating this button on Hold
  // ONLY meant it then disappeared, leaving no bound-payment path: the
  // cashier re-rang the items as a fresh sale (new order id, original order
  // left open + unpaid). Gate on "any non-locked status" so a New-status
  // (already-flipped) order stays recoverable onto its own receipt — the
  // bound /cart/commit accepts New and Hold alike (cart.py _assert_mutable
  // refuses only Paid/Paying/Canceled/Deleted). Not gated on kiosk_status so
  // a clerk can skip the prep step on simple orders.
  const canFinish =
    isEcomOrder && !LOCKED_RECEIPT_STATUSES.has(Number(merged.status)) && canMutateHere

  // Resume a non-ecom held POS receipt — load it into the cart so the
  // cashier can finish/pay it. Mirrors the OrdersTab card play-button
  // flow that previously lived per-row.
  const canResumeHeld =
    !isEcomOrder
    && receiptId != null
    && Boolean(merged.on_hold)
    && !LOCKED_RECEIPT_STATUSES.has(Number(merged.status))
    && canMutateHere

  // Cancel an unpaid order. Backend rejects cancel-order on paid receipts
  // (HTTP 409, code: cannot_cancel_paid) and on terminal statuses
  // (Canceled, Deleted), so gate on the locked-status set. Available for
  // ecom orders (any non-locked status) and held POS receipts.
  const canCancelOrder =
    receiptId != null
    && !LOCKED_RECEIPT_STATUSES.has(Number(merged.status))
    && (isEcomOrder || Boolean(merged.on_hold))
    && canMutateHere

  const handleCancelOrder = useCallback(() => {
    if (receiptId == null) return
    const cancelMessage = isEcomOrder
      ? `Order #${merged.number || receiptId} will be canceled and the customer will be emailed. This cannot be undone.`
      : `Order #${merged.number || receiptId} will be canceled. This cannot be undone.`
    showConfirm({
      title: 'Cancel this order?',
      message: cancelMessage,
      confirmLabel: 'Cancel Order',
      cancelLabel: 'Keep Order',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await cancelOrder.mutateAsync({ receiptId })
          showToast({
            type: 'success',
            title: 'Order canceled',
            message: `Order #${merged.number || receiptId} has been canceled.`,
          })
          onClose()
        } catch (err) {
          showToast({
            type: 'error',
            title: 'Could not cancel order',
            message: err instanceof Error ? err.message : undefined,
          })
        }
      },
    })
  }, [cancelOrder, receiptId, merged.number, showConfirm, showToast, onClose, isEcomOrder])

  const handleResumeHeld = useCallback(async () => {
    if (receiptId == null) return
    try {
      await recallReceipt(receiptId)
      // Drop the cashier on the Products tab so they can immediately
      // continue editing the recalled cart, mirroring the ecom Finish
      // Sale flow in handleFinishSale.
      setActiveTab('products')
      onClose()
      showToast({
        type: 'success',
        title: 'Order Resumed',
        message: `Order #${merged.number || receiptId} loaded into cart.`,
      })
    } catch (err) {
      showToast({
        type: 'error',
        title: 'Could not resume order',
        message: err instanceof Error ? err.message : undefined,
      })
    }
  }, [recallReceipt, receiptId, merged.number, setActiveTab, onClose, showToast])

  const handleFinishSale = useCallback(async () => {
    if (receiptId == null) return
    // Online-PAID (PaidOnline=32): the money is already captured. Load the
    // order into the POS READ-ONLY (no status flip, no edits) so the
    // cashier can verify the items; the cart's Checkout button renders as
    // "Complete" and runs /complete-pickup — no payment modal. Cancel (with
    // automatic gateway refund) stays available from this modal.
    if (Number(merged.status) === RECEIPT_STATUS.PAID_ONLINE) {
      try {
        await loadPaidOnline(receiptId)
        setActiveTab(isMobile ? 'cart' : 'products')
        onClose()
        if (isMobile) navigate({ to: '/pos' })
        showToast({
          type: 'success',
          title: 'Order loaded (paid online)',
          message: `Order #${merged.number || receiptId} — verify the items, then press Complete. No payment to take.`,
        })
      } catch (err) {
        showToast({
          type: 'error',
          title: 'Could not load order',
          message: err instanceof Error ? err.message : undefined,
        })
      }
      return
    }
    try {
      // Pass the pre-load status so a later POS "Close" reverts the order to
      // exactly where it was (normally Hold) instead of cancelling it.
      const { receipt: finished } = await finishEcomSale(
        receiptId, Number(merged.status),
      )
      // Mobile: land on the cart/checkout view and jump to the POS — the modal
      // may be open from the deliveries board or driver mode, not /pos, so the
      // driver needs to be taken there to take payment. Desktop: keep the
      // cashier on Products; the cart lives in the always-visible sidebar.
      setActiveTab(isMobile ? 'cart' : 'products')
      onClose()
      if (isMobile) navigate({ to: '/pos' })
      showToast({
        type: 'success',
        title: 'Order loaded into POS',
        message: `Order #${finished.number || receiptId} ready for payment.`,
      })
    } catch (err) {
      showToast({
        type: 'error',
        title: 'Could not load order',
        message: err instanceof Error ? err.message : undefined,
      })
    }
  }, [finishEcomSale, loadPaidOnline, receiptId, merged.status, merged.number, setActiveTab, onClose, showToast, isMobile, navigate])

  // Refund eligibility — only show the button when this receipt can actually
  // be refunded. We hide it in three cases:
  //   1. The receipt is itself a refund (`is_return` / `src_id` / source
  //      pointers populated). Refunds-of-refunds aren't a thing.
  //   2. The receipt has already been refunded (legacy `return_*` pointers
  //      populated). Backend would reject a second /copy-with-refund.
  //   3. The receipt isn't in a Paid state. Open / on-hold / canceled /
  //      voided receipts can't be refunded — there's nothing to return.
  const isRefundReceipt = Boolean(
    merged.is_return === true
    || merged.src_id
    || merged.src_number
    || merged.source_number,
  )
  const alreadyRefunded = Boolean(
    merged.return_receipt_id
    || merged.return_number
    || merged.return_total,
  )
  // Split refunds: previously-refunded receipts stay refundable -- the BE
  // seeds remaining quantities only, and handleRefund guards the
  // empty-seed / old-BE cases (refund-integrity, waio-36-birchmount-refunds).
  const isPaidReceipt = Number(merged.status) === RECEIPT_STATUS.PAID && !merged.on_hold
  const canRefund = receiptId != null && isPaidReceipt && !isRefundReceipt && canMutateHere
  // Every line fully refunded → gray out the Refund button instead of
  // letting the BE create a stub return we'd immediately void. Reads
  // receipt.items only (the transaction payload lacks returned_qty);
  // on old backends the field is absent → 0 → button stays enabled and
  // the "Nothing Left to Refund" stub-void guard still catches it.
  const isFullyRefundedReceipt =
    (receipt?.items?.length ?? 0) > 0 &&
    (receipt?.items ?? []).every((i) => {
      const q = Number(i.qty ?? 0)
      return q <= 0 || Number(i.returned_qty ?? 0) >= q
    })
  // Online-paid orders: the one-click full-refund + cancel-order fast
  // path is only valid while the order is UNTOUCHED — no gateway credit
  // taken yet and no return receipts. After any partial, only the
  // item-level path remains (the BE rejects the full path with
  // invalid_amount, and a partially-refunded order can't be cancelled).
  const isOnlinePaid = merged.has_captured_online_payment === true
  const onlineUntouched =
    isOnlinePaid &&
    !alreadyRefunded &&
    !(Number(receipt?.online_payment?.refunded_amount ?? 0) > 0)

  // Untouched online-paid orders have two refund paths, so the single
  // footer "Refund" button opens RefundOptionsDialog to pick one. Every
  // other order goes straight to handleRefund.
  const [refundOptionsOpen, setRefundOptionsOpen] = useState(false)

  // FULL refund of an online-paid order: gateway credit to the card that
  // paid (card-not-present) + the backend cancels the order. Only offered
  // while the order is untouched (no partial refunds yet) — the BE rejects
  // it otherwise, because cancelling a partially-refunded order would
  // corrupt reporting/inventory. Runs straight from RefundOptionsDialog,
  // which is the confirmation step.
  const handleRefundOnlineFull = useCallback(async () => {
    if (receiptId == null) return
    try {
      const res = await refundOnline.mutateAsync({ receiptId })
      const kind = res?.data?.refund?.kind
      showToast({
        type: 'success',
        title: 'Refunded to card',
        message:
          kind === 'void'
            ? 'Same-day void — the charge will not settle on their statement.'
            : 'Refund sent to the customer’s card; it can take a few business days to appear.',
      })
      setRefundOptionsOpen(false)
      onClose()
    } catch (err) {
      showToast({
        type: 'error',
        title: 'Card refund failed',
        message: err instanceof Error ? err.message : undefined,
      })
    }
  }, [receiptId, refundOnline, showToast, onClose])

  // Item-level refund via copy-with-refund — for online-paid orders too:
  // the BE seeds the still-refundable items and surfaces the online
  // capture as a synthetic source payment the checkout can credit at the
  // gateway (Online Card tender) or pay out as cash.
  const handleRefund = useCallback(async () => {
    if (receiptId == null) return
    try {
      // Mirrors FunctionsTab.handleRefundReceiptSelected — same /copy-with-
      // refund call, same store-bootstrap sequence (enterRefundMode →
      // setServerTotals → loadRefundItems). Kept inline rather than
      // extracted into a shared hook to keep this change surgical; if a
      // third call site appears we should refactor both into useStartRefund.
      const refund = await createRefund.mutateAsync({
        sourceReceiptId: receiptId,
        isReturn: true,
      })

      // Split-refund guards -- mirrors FunctionsTab.handleRefundReceiptSelected
      // (see the comment there; same empty-seed / old-BE protection).
      const hasNewBeContract = (refund.source_payments ?? []).some(
        (sp: any) => sp?.jt_id != null || sp?.remaining != null,
      )
      if ((refund.items ?? []).length === 0) {
        showToast({
          type: 'info',
          title: 'Nothing Left to Refund',
          message: 'All items on this receipt have already been returned.',
        })
        // Don't strand the just-created return receipt (it would sit in
        // Orders as a live New return -- and on an old BE, a full-cart
        // completion path for the exact double-refund this guard stops).
        // Best-effort: already_deleted (409) is benign.
        if (refund.id) {
          try { await voidReceipt.mutateAsync({ receiptId: refund.id }) } catch { /* benign */ }
        }
        return
      }
      if (alreadyRefunded && !hasNewBeContract) {
        showToast({
          type: 'warning',
          title: 'Refund Blocked',
          message: 'This receipt was already refunded and the server does '
            + 'not support split refunds yet. Contact support.',
        })
        // Don't strand the just-created return receipt (it would sit in
        // Orders as a live New return -- and on an old BE, a full-cart
        // completion path for the exact double-refund this guard stops).
        // Best-effort: already_deleted (409) is benign.
        if (refund.id) {
          try { await voidReceipt.mutateAsync({ receiptId: refund.id }) } catch { /* benign */ }
        }
        return
      }

      const { enterRefundMode, loadRefundItems, setServerTotals } =
        usePOSStore.getState()

      enterRefundMode(refund, refund.source_payments ?? null)

      if (refund.totals) {
        const t = refund.totals as Record<string, unknown> & {
          subtotal?: number | string
          deposit_amount?: number | string
          recycling_amount?: number | string
          total?: number | string
        }
        const subtotal = Number(t.subtotal ?? 0)
        const taxes = extractTaxes(t)
        const deposit = Number(t.deposit_amount ?? t.recycling_amount ?? 0)
        const total = Number(t.total ?? 0)
        const amountToPay = Number(refund.amount_to_pay ?? total)
        setServerTotals({ subtotal, taxes, deposit, total, amountToPay })
      } else {
        setServerTotals(null)
      }

      // Also seed cartStore.preview so the CFD (which broadcasts
      // cart.preview.totals, not posStore.serverTotals) shows real
      // tax / total instead of falling back to lines-only subtotal.
      seedRefundPreview(refund)

      // Enrich refund items with full Product data (image, brand, tags,
      // strain, THC%, IOH) so cart rows render the same as fresh
      // additions. Mirrors the on-hold recall flow.
      const productMap = refund.items && refund.items.length > 0
        ? await fetchProductsForRefundItems(
            refund.items as unknown as Parameters<typeof fetchProductsForRefundItems>[0],
            queryClient,
          )
        : new Map()

      if (refund.items && refund.items.length > 0) {
        loadRefundItems(refund.items, productMap)
      }

      // Close the order detail and switch to the products tab so the
      // cashier sees the refund cart on the right and can proceed to
      // checkout.
      setActiveTab('products')
      onClose()
      showToast({
        type: 'success',
        title: 'Refund Mode',
        message: `Loaded ${refund.items?.length ?? 0} refund items`,
      })
    } catch (err) {
      showToast({
        type: 'error',
        title: 'Refund failed',
        message: err instanceof Error ? err.message : 'Could not start refund',
      })
    }
  }, [
    receiptId,
    createRefund,
    queryClient,
    setActiveTab,
    onClose,
    showToast,
    alreadyRefunded,
    voidReceipt,
  ])

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-60 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={handleCopyOrderNumber}
                title={orderNumberCopied ? 'Copied' : 'Click to copy order number'}
                className="group flex items-center gap-1.5 text-lg font-bold text-gray-900 leading-tight cursor-pointer hover:text-gray-700 transition-colors"
              >
                <span>Order #{headerNumber}</span>
                {orderNumberCopied ? (
                  <Check size={14} className="text-green-600" />
                ) : (
                  <Copy size={14} className="text-gray-400 group-hover:text-gray-600 transition-colors" />
                )}
              </button>
              {merged.on_hold && (
                <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">
                  On hold
                </span>
              )}
              {isForeignStore && (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700"
                  title="Rung at another store — view only here"
                >
                  <MapPin size={12} />
                  {foreignStoreName}
                </span>
              )}
              {items.length > 0 && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                  {items.length} item{items.length === 1 ? '' : 's'}
                </span>
              )}
              {ecomFulfillment && (
                <span
                  className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                    ecomFulfillment === 'delivery'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-teal-100 text-teal-700'
                  }`}
                >
                  {ecomFulfillment === 'delivery' ? 'Delivery' : 'Pickup'}
                </span>
              )}
              {kioskStatus != null && <KioskStatusBadge status={kioskStatus} />}
              {/* Prepaid online order awaiting pickup — make "no payment
                  due at the counter" unmissable. Refunded orders lose
                  the chip (has_captured_online_payment goes false). */}
              {Number(merged.status) === RECEIPT_STATUS.PAID_ONLINE
                && merged.has_captured_online_payment !== false && (
                <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">
                  Paid
                </span>
              )}
            </div>
            <div className="flex items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-500 flex-wrap">
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                {formatDateTime(merged.timestamp)}
              </span>
              {customerName && (
                handleCustomerClick ? (
                  <button
                    type="button"
                    onClick={handleCustomerClick}
                    className="flex items-center gap-1 text-gray-500 hover:text-green-700 hover:underline underline-offset-2 cursor-pointer"
                    title="Open customer profile"
                  >
                    <UserIcon size={12} />
                    {customerName}
                  </button>
                ) : (
                  <span className="flex items-center gap-1">
                    <UserIcon size={12} />
                    {customerName}
                  </span>
                )
              )}
              {(merged.register_name || merged.user_name || merged.clerk_name) && (
                <span className="flex items-center gap-1">
                  <MonitorSmartphone size={12} />
                  {merged.register_name}
                  {merged.register_name && (merged.user_name || merged.clerk_name) ? ' · ' : ''}
                  {merged.user_name || merged.clerk_name}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer shrink-0"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {isLoading && (
            <div className="flex items-center justify-center py-10 text-sm text-gray-400">
              <Loader2 size={16} className="animate-spin mr-2" /> Loading order…
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-2">
              <AlertCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-red-700">Couldn't load order</p>
                <p className="text-xs text-red-600 mt-0.5">
                  {error instanceof Error ? error.message : 'Please try again.'}
                </p>
              </div>
            </div>
          )}

          {!isLoading && !error && items.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 text-gray-400">
              <ReceiptIcon size={32} className="mb-2" />
              <p className="text-sm font-medium">No items on this order</p>
            </div>
          )}

          {items.length > 0 && (
            <ul className="space-y-2">
              {items.map((item, idx) => {
                const key = getItemKey(item, idx)
                const productId = getItemProductId(item)
                return (
                  <OrderLineRow
                    key={key}
                    item={item}
                    product={productId != null ? products[productId] : undefined}
                    state={addStates[key] ?? { kind: 'idle' }}
                    isBusy={bulkAdding}
                    onAdd={() => addLine(item, key)}
                    hideAdd={hideAddActions}
                    hideOutOfStock={hideOutOfStock}
                  />
                )
              })}
            </ul>
          )}

          {/* Delivery: current status + action buttons + change timeline.
              Only renders for delivery orders, turning the shared order
              modal into the "delivery version" the workflow needs. */}
          {merged.is_delivery === true && receiptId != null && (
            <DeliveryStatusPanel
              receiptId={receiptId}
              readOnly={!canMutateHere}
              currentStatus={
                typeof merged.delivery_status === 'string'
                  ? merged.delivery_status
                  : DELIVERY_STATUS.NONE
              }
            />
          )}
        </div>

        {/* Totals + payments — two-column layout to keep the modal short:
            payments stacked on the left, subtotal/tax/total on the right.
            Mirrors the receipt printout's two-column footer. */}
        {items.length > 0 && (
          <div className="border-t border-gray-100 bg-gray-50">
            <div className="px-5 py-3 grid grid-cols-2 divide-x divide-gray-200 text-sm">
              {/* Left column: payments + Paid/Change rollups. Empty
                  placeholder when no payments so the right column stays
                  visually anchored. */}
              <div className="pr-6">
                {payments.length > 0 ? (
                  <>
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                      Payment
                    </p>
                    <ul className="space-y-1">
                      {payments
                        // Skip the CHANGE JT row -- we already render the
                        // change as a dedicated summary line below
                        // (driven by `change_due`), so listing the JT
                        // here too would double-render "Change $X" on
                        // any cash overpay receipt.
                        .filter((p) => p.payment_type !== PAYMENT_TYPES.CHANGE)
                        .map((p, idx) => {
                        const amount = toNumber(p.amount)
                        return (
                          <li
                            key={p.id ?? `pay-${idx}`}
                            className="flex items-center justify-between"
                          >
                            <span className="text-gray-700 flex items-center gap-1.5">
                              <PaymentMethodLabel
                                label={getPaymentLabel(p)}
                                paymentType={p.payment_type}
                                wallet={p.wallet}
                                last4={p.last4}
                              />
                              {p.status && p.status !== 'success' && p.status !== 'completed' && (
                                <span className="text-[10px] uppercase tracking-wide text-amber-600 bg-amber-50 px-1 py-0.5 rounded">
                                  {p.status}
                                </span>
                              )}
                            </span>
                            {/* Amount stacked over the tip note --
                                inline, the note wrapped mid-phrase at
                                modal width and collided with the
                                method/last4 column. */}
                            <span className="flex flex-col items-end">
                              <span className="text-gray-900 font-medium tabular-nums">
                                {formatCurrency(amount)}
                              </span>
                              {toNumber(p.tip_amount) > 0.005 && (
                                <span className="text-gray-400 text-xs font-normal whitespace-nowrap">
                                  incl. {formatCurrency(toNumber(p.tip_amount))} tip
                                </span>
                              )}
                            </span>
                          </li>
                        )
                      })}
                      {/* BE `amount_paid` sums payment JT `price`
                          (pre-tip base, serialize.py:243 ->
                          actually_paid_amount), so on a tipped receipt
                          it equals the pre-tip `total` and this row
                          stays hidden. Also test against the
                          tip-inclusive displayed total so the row
                          can't misfire if that semantic ever changes
                          to jamount. */}
                      {/* Suppress entirely for prepaid online orders:
                          `amount_paid` sums payment JTs, which don't
                          exist for gateway captures, so it reads $0.00
                          and contradicts the Online row above it. */}
                      {jtPayments.length > 0
                        && amountPaid !== null && total !== null
                        && Math.abs(amountPaid - total) > 0.01
                        && Math.abs(amountPaid - (total + terminalTipComponent)) > 0.01 && (
                        <li className="flex items-center justify-between pt-1 mt-1 border-t border-gray-200">
                          <span className="text-gray-500">Paid</span>
                          <span className="text-gray-500 tabular-nums">
                            {formatCurrency(amountPaid)}
                          </span>
                        </li>
                      )}
                      {changeDue !== null && changeDue > 0 && (
                        <li className="flex items-center justify-between">
                          <span className="text-gray-500">Change</span>
                          <span className="text-gray-500 tabular-nums">
                            {formatCurrency(changeDue)}
                          </span>
                        </li>
                      )}
                    </ul>
                  </>
                ) : (
                  <span />
                )}
              </div>

              {/* Right column: order totals. Subtotal / Discount /
                  Deposit / Tax / Total stack so the customer's eye
                  tracks straight to the final number. */}
              <dl className="space-y-1 pl-6">
                {subtotal !== null && (
                  <TotalRow label="Subtotal" value={formatCurrency(subtotal)} />
                )}
                {discount !== null && discount > 0 && (
                  <TotalRow
                    label="Discount"
                    value={`-${formatCurrency(discount)}`}
                    valueClass="text-green-700"
                  />
                )}
                {discount !== null && discount > 0 && discountApprovedBy && (
                  <div className="text-xs text-gray-400 -mt-0.5 text-right">{discountApprovedBy}</div>
                )}
                {deposit !== null && Math.abs(deposit) > 0.005 && (
                  <TotalRow label="Deposit" value={formatCurrency(deposit)} />
                )}
                {tax !== null && Math.abs(tax) > 0.005 && (
                  <TotalRow label="Tax" value={formatCurrency(tax)} />
                )}
                {tipsDisplay > 0.005 && (
                  <TotalRow label="Tips" value={formatCurrency(tipsDisplay)} />
                )}
                {total !== null && (
                  <div className="flex items-center justify-between pt-1.5 mt-1.5 border-t border-gray-200">
                    <dt className="text-gray-700 font-semibold">Total</dt>
                    <dd className="text-gray-900 font-bold text-base tabular-nums">
                      {/* Terminal tips are outside receipt.total; add
                          them so the Total matches what the card was
                          actually charged (cart tips are already in). */}
                      {formatCurrency(total + terminalTipComponent)}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {isRefundReceipt && (() => {
              // Resolve the source pointers — different shapes for v1
              // legacy (`src_*`) vs v2 (`source_*`). Either form gets us
              // back to the original sale.
              const srcNumber =
                (typeof merged.src_number === 'string' && merged.src_number)
                || (typeof merged.source_number === 'string' && merged.source_number)
                || null
              const srcId =
                typeof merged.src_id === 'number' ? merged.src_id : undefined
              if (!srcNumber && srcId == null) return null
              // Click is enabled whenever we have either a numeric id
              // OR a receipt number. The parent resolves a number to
              // an id via /receipts/search when needed (the BE doesn't
              // always populate `src_id` on refund receipts).
              const canJump = Boolean(onOpenReceipt) && (srcId != null || !!srcNumber)
              return (
                <div className="px-5 pt-3 pb-3 border-t border-gray-200">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                      Refunded
                    </span>
                    <span className="text-sm text-gray-500">Source:</span>
                    {canJump ? (
                      <button
                        type="button"
                        onClick={() =>
                          onOpenReceipt?.({
                            receiptId: srcId,
                            receiptNumber: srcNumber ?? undefined,
                          })
                        }
                        className="text-sm font-medium text-blue-600 hover:underline cursor-pointer"
                      >
                        #{srcNumber ?? srcId}
                      </button>
                    ) : (
                      <span className="text-sm font-medium text-gray-700">
                        #{srcNumber ?? srcId}
                      </span>
                    )}
                  </div>
                </div>
              )
            })()}
          </div>
        )}

        {/* Footer */}
        {/* Mobile: a 2-col grid of full-width buttons (wraps cleanly, no
            cut-off, thumb-sized) with a Close button in thumb reach. Desktop:
            right-aligned single-line actions that wrap as a row rather than
            breaking labels; Close is hidden (header X / Esc / backdrop).
            `contents` flattens the action group into the grid on mobile so
            every button is a direct grid cell. */}
        <div className="border-t border-gray-100 p-4 bg-white grid grid-cols-2 gap-2 md:flex md:items-center md:justify-end [&_button]:w-full [&_button]:justify-center md:[&_button]:w-auto md:[&_button]:whitespace-nowrap">
          <button
            onClick={onClose}
            className="text-sm px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 font-medium rounded-xl transition-colors cursor-pointer order-last md:hidden"
          >
            Close
          </button>
          <div className="contents md:flex md:flex-wrap md:items-center md:justify-end md:gap-2">
            {canRefund && (
              <button
                onClick={onlineUntouched ? () => setRefundOptionsOpen(true) : handleRefund}
                disabled={createRefund.isPending || isFullyRefundedReceipt}
                title={isFullyRefundedReceipt ? 'Every item on this receipt has been refunded' : undefined}
                className="text-sm inline-flex items-center gap-2 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {createRefund.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Undo2 size={16} />
                )}
                {isFullyRefundedReceipt
                  ? 'Fully refunded'
                  : createRefund.isPending
                    ? 'Starting…'
                    : 'Refund'}
              </button>
            )}
            {canPrepare && receiptId != null && (
              <button
                onClick={handlePrepare}
                disabled={printPrepSlip.isPending || setKioskStatus.isPending}
                className="text-sm inline-flex items-center gap-2 px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                title="Mark this order as Preparing (prints a prep slip if a printer is available)"
              >
                {printPrepSlip.isPending || setKioskStatus.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <PackageOpen size={16} />
                )}
                {printPrepSlip.isPending || setKioskStatus.isPending
                  ? 'Preparing…'
                  : 'Prepare'}
              </button>
            )}
            {canMarkReady && receiptId != null && (
              <button
                onClick={() => setMessageDialog({ open: true, mode: 'ready' })}
                className="text-sm inline-flex items-center gap-2 px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl transition-colors cursor-pointer"
                title="Send the customer a 'your order is ready' message and mark Ready"
              >
                <MessageSquare size={16} />
                Ready
              </button>
            )}
            {canRemind && receiptId != null && (
              <button
                onClick={() => setMessageDialog({ open: true, mode: 'remind' })}
                className="text-sm inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-green-200 text-green-700 hover:bg-green-50 font-semibold rounded-xl transition-colors cursor-pointer"
                title="Resend the order-ready message to the customer"
              >
                <MessageSquare size={16} />
                Follow Up
              </button>
            )}
            {canCancelOrder && (
              <button
                onClick={handleCancelOrder}
                disabled={cancelOrder.isPending}
                className="text-sm inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-red-200 text-red-600 hover:bg-red-50 font-semibold rounded-xl transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                title={isEcomOrder ? 'Cancel this online order and email the customer' : 'Cancel this order'}
              >
                {cancelOrder.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Ban size={16} />
                )}
                {cancelOrder.isPending ? 'Canceling…' : 'Cancel'}
              </button>
            )}
            {canFinish && receiptId != null && (
              <button
                onClick={handleFinishSale}
                disabled={isFinishing || isLoadingPaidOnline}
                className="text-sm inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                title={
                  Number(merged.status) === RECEIPT_STATUS.PAID_ONLINE
                    ? 'Order was paid online — load it read-only to verify the items, then press Complete (no payment to take)'
                    : 'Load this order into the POS so the cashier can take payment'
                }
              >
                {isFinishing || isLoadingPaidOnline ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Play size={16} />
                )}
                {isFinishing || isLoadingPaidOnline
                  ? 'Loading…'
                  : Number(merged.status) === RECEIPT_STATUS.PAID_ONLINE
                    ? 'View & Complete'
                    : 'Complete'}
              </button>
            )}
            {canResumeHeld && (
              <button
                onClick={handleResumeHeld}
                disabled={isRecalling}
                className="text-sm inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                title="Load this order into the cart so the cashier can take payment"
              >
                {isRecalling ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Play size={16} />
                )}
                {isRecalling ? 'Loading…' : 'Complete'}
              </button>
            )}
            {isForeignStore && (
              <span
                className="text-xs text-gray-500 md:self-center md:mr-2 col-span-2 text-center md:text-left"
                title="Refunds, voids and reprints run against the originating store's till, inventory and card terminal."
              >
                Rung at {foreignStoreName ?? 'another store'} · refund, void and reprint there
              </span>
            )}
            {receiptId != null && canMutateHere && (
              <button
                onClick={handlePrint}
                disabled={printReceipt.isPending}
                className="text-sm inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 font-medium rounded-xl transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {printReceipt.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Printer size={16} />
                )}
                {printReceipt.isPending ? 'Printing…' : 'Print'}
              </button>
            )}
            {!hideAddActions && (
              <button
                onClick={handleAddAll}
                disabled={bulkAdding || items.length === 0 || allAdded || remainingAddable === 0}
                className="text-sm inline-flex items-center gap-2 px-4 py-2.5 bg-green-500 hover:bg-green-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-semibold rounded-xl transition-colors cursor-pointer disabled:cursor-not-allowed"
              >
                {bulkAdding ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : allAdded ? (
                  <Check size={16} />
                ) : (
                  <Plus size={16} />
                )}
                {bulkAdding
                  ? 'Adding…'
                  : allAdded
                    ? 'All added'
                    : remainingAddable === items.length
                      ? `Add all to order`
                      : `Add ${remainingAddable} remaining`}
              </button>
            )}
          </div>
        </div>
      </div>
      {receiptId != null && messageDialog.open && (
        <CustomerMessageDialog
          open={messageDialog.open}
          onClose={() => setMessageDialog({ open: false })}
          mode={messageDialog.mode}
          receiptId={receiptId}
          receiptNumber={merged.number ?? null}
          customerId={customerId}
          customerDisplayName={customerName}
          items={items.map((it) => ({
            name: getItemName(it),
            qty: getItemQty(it),
          }))}
          isDelivery={merged.is_delivery === true}
          onSent={messageDialog.mode === 'ready' ? handleAfterReadySent : async () => {}}
          onMarkReadyWithoutMessage={
            messageDialog.mode === 'ready' ? handleMarkReadyWithoutMessage : undefined
          }
        />
      )}
      {refundOptionsOpen && (
        <RefundOptionsDialog
          orderNumber={String(merged.number || receiptId)}
          amountLabel={formatCurrency(toNumber(receipt?.online_payment?.amount ?? total))}
          cardLastFour={receipt?.online_payment?.card_last_four ?? null}
          fullPending={refundOnline.isPending}
          itemsPending={createRefund.isPending}
          onClose={() => setRefundOptionsOpen(false)}
          onFullRefund={handleRefundOnlineFull}
          onChooseItems={handleRefund}
        />
      )}
    </div>
  )
}

// Pipeline-state badge for an ecom receipt's `kiosk_status`. Mirrors the
// numeric enum at project/frontend/models/pos.py:KioskReceiptStatus
// (0=New, 1=Preparing, 2=Ready, 3=Completed). The Ready stage is the
// one cashiers care about visually — the customer has been notified
// and is on their way — so it's the only green pill.
function KioskStatusBadge({ status }: { status: number }) {
  const styles: Record<number, { label: string; className: string }> = {
    0: { label: 'New', className: 'bg-yellow-100 text-yellow-800' },
    1: { label: 'Preparing', className: 'bg-blue-100 text-blue-700' },
    2: { label: 'Ready', className: 'bg-green-100 text-green-700' },
    3: { label: 'Completed', className: 'bg-gray-200 text-gray-700' },
  }
  const s = styles[status]
  if (!s) return null
  return (
    <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${s.className}`}>
      {s.label}
    </span>
  )
}

// ---------- Line row ----------

function OrderLineRow({
  item,
  product,
  state,
  isBusy,
  onAdd,
  hideAdd = false,
  hideOutOfStock = false,
}: {
  item: AnyItem
  product: Product | null | undefined
  state: AddState
  isBusy: boolean
  onAdd: () => void
  hideAdd?: boolean
  // The store's frontend.hide_out_of_stock setting — decides whether the
  // merchandising flags count as "can't be sold here" (see getStockState).
  hideOutOfStock?: boolean
}) {
  const name = getItemName(item)
  const brand = getItemBrand(item)
  const size = getItemSize(item)
  const sku = getItemSku(item)
  const image = getItemImage(item, product ?? undefined)
  const productId = getItemProductId(item)
  // Special lines (gift card, Delivery, ...) carry no inventory — force "in"
  // so the red badge never shows. Everything else reflects the looked-up
  // product's on-hand.
  // `null` = looked up and not carried at this store (exact-id lookup
  // found nothing); `undefined` = lookup still pending.
  const stock = isSpecialProductId(productId)
    ? 'in'
    : product === null
      ? 'out'
      : getStockState(product ?? undefined, hideOutOfStock)
  const qty = getItemQty(item)
  const unitPrice = getItemUnitPrice(item)
  const lineSubtotal = getItemSubtotal(item)
  // On refund clones from /copy-with-refund the BE doesn't echo
  // `discount_amount` onto the items, so the green "-$29.98" badge that
  // showed on the original sale disappears and the cashier sees a
  // "−1 × $124.66" line with a $-94.68 subtotal that doesn't reconcile.
  // Derive the discount from the gap when the declared field is missing
  // so refund lines render the same way as the originals.
  const declaredDiscount = getItemDiscount(item)
  const derivedDiscount = Math.max(0, Math.abs(unitPrice * qty) - Math.abs(lineSubtotal))
  const lineDiscount = declaredDiscount > 0 ? declaredDiscount : derivedDiscount
  const lot = getItemLot(item)
  const isLinkable = productId != null

  // Refund state on the SOURCE receipt (returned_qty comes from the v2
  // receipt payload; refund receipts have qty < 0 so they never match).
  const returnedQty = getItemReturnedQty(item)
  const isFullyRefunded = qty > 0 && returnedQty >= qty
  const isRefunded = qty > 0 && returnedQty > 0
  const struck = isRefunded ? 'line-through text-gray-400' : ''

  const meta = [brand, size, sku ? `#${sku}` : null].filter(Boolean).join(' · ')

  // Why this line can't be added, in the cashier's words. Replaces the
  // generic "Out of stock" badge when present -- it is the same fact,
  // said precisely ("Not carried at this store", "No price at this
  // store"), and two red badges saying it twice reads as two problems.
  const unavailableMessage = state.kind === 'unavailable' ? (state.message ?? null) : null

  const isOut = !isLinkable || stock === 'out' || state.kind === 'unavailable'
  const isAdded = state.kind === 'added'
  const isLoading = state.kind === 'loading' || (isBusy && !isAdded && !isOut)

  return (
    <li
      className={`flex items-stretch gap-3 p-3 border border-gray-100 rounded-xl hover:border-gray-200 transition-colors ${
        isRefunded ? 'bg-gray-50 opacity-70' : 'bg-white'
      }`}
    >
      {/* Image */}
      <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden">
        {image ? (
          // eslint-disable-next-line jsx-a11y/alt-text
          <img
            src={withCdnImageWidth(image, 100)}
            alt=""
            className={`w-full h-full object-cover ${isRefunded ? 'grayscale' : ''}`}
          />
        ) : (
          <ImageOff size={18} className="text-gray-300" />
        )}
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <div className="flex items-start justify-between gap-3">
          <p className={`text-sm font-semibold truncate leading-tight ${struck || 'text-gray-900'}`}>
            {name}
          </p>
          <p className={`text-sm font-bold tabular-nums shrink-0 ${struck || 'text-gray-900'}`}>
            {formatCurrency(lineSubtotal)}
          </p>
        </div>
        {meta && (
          <p className="text-xs text-gray-500 truncate mt-0.5 select-text">{meta}</p>
        )}
        <div className="flex items-center justify-between gap-3 mt-1.5">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className={`tabular-nums ${struck}`}>
              {qty} × {formatCurrency(unitPrice)}
            </span>
            {isRefunded && (
              <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 font-medium whitespace-nowrap">
                {isFullyRefunded ? 'Refunded' : `${returnedQty} of ${qty} refunded`}
              </span>
            )}
            {lot && (
              <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-semibold tabular-nums select-text">
                {lot}
              </span>
            )}
            {lineDiscount >= 0.005 && (
              <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-700 font-medium tabular-nums">
                -{formatCurrency(lineDiscount)}
              </span>
            )}
            {stock === 'out' && !unavailableMessage && (
              <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-medium">
                Out of stock
              </span>
            )}
            {unavailableMessage && (
              <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-medium">
                {unavailableMessage}
              </span>
            )}
            {state.kind === 'error' && (
              <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-medium" title={state.message}>
                Failed
              </span>
            )}
          </div>

          {!hideAdd && !isFullyRefunded && (
            <AddButton
              isAdded={isAdded}
              isLoading={isLoading}
              isOut={isOut}
              onClick={onAdd}
              qty={qty}
            />
          )}
        </div>
      </div>
    </li>
  )
}

function AddButton({
  isAdded,
  isLoading,
  isOut,
  onClick,
  qty,
}: {
  isAdded: boolean
  isLoading: boolean
  isOut: boolean
  onClick: () => void
  qty: number
}) {
  if (isAdded) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-green-50 text-green-700 text-xs font-semibold border border-green-100">
        <Check size={12} /> Added
      </span>
    )
  }
  if (isOut) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-50 text-gray-400 text-xs font-medium border border-gray-100">
        <PackageX size={12} /> Unavailable
      </span>
    )
  }
  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-green-500 hover:text-white text-gray-700 text-xs font-semibold transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {isLoading ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
      {isLoading ? 'Adding' : `Add${qty > 1 ? ` ×${qty}` : ''}`}
    </button>
  )
}

function TotalRow({
  label,
  value,
  valueClass = 'text-gray-900',
}: {
  label: string
  value: string
  valueClass?: string
}) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-gray-500">{label}</dt>
      <dd className={`font-medium tabular-nums ${valueClass}`}>{value}</dd>
    </div>
  )
}

// Tint for a delivery status chip / timeline dot. Mirrors the OrdersTab
// helper; kept local so the modal doesn't import card internals.
function deliveryStatusTint(status: string): string {
  switch (status) {
    case DELIVERY_STATUS.DELIVERED:
      return 'bg-green-100 text-green-700'
    case DELIVERY_STATUS.OUT_FOR_DELIVERY:
      return 'bg-blue-100 text-blue-700'
    case DELIVERY_STATUS.DELIVERY_FAILED:
      return 'bg-red-100 text-red-700'
    case DELIVERY_STATUS.PICKUP_AT_STORE:
      return 'bg-amber-100 text-amber-800'
    case DELIVERY_STATUS.PREPARING:
      return 'bg-indigo-100 text-indigo-700'
    default:
      return 'bg-gray-100 text-gray-600'
  }
}

// Contextual in-transit actions for the current delivery status. "Out for
// Delivery Again" is a retry — another out_for_delivery transition the BE
// log records as a fresh attempt. There is no "Delivered" action: delivery
// completion runs through the POS payment flow (the Complete button below),
// and a fully-paid delivery receipt is marked delivered by the backend.
function modalDeliveryActions(
  status: string,
): Array<{ label: string; status: DeliveryStatus; tone: string }> {
  const out = { label: 'Out for Delivery', status: DELIVERY_STATUS.OUT_FOR_DELIVERY, tone: 'bg-blue-600 hover:bg-blue-700 text-white' }
  const outAgain = { label: 'Out for Delivery Again', status: DELIVERY_STATUS.OUT_FOR_DELIVERY, tone: 'bg-blue-600 hover:bg-blue-700 text-white' }
  const failed = { label: 'Failed', status: DELIVERY_STATUS.DELIVERY_FAILED, tone: 'bg-red-600 hover:bg-red-700 text-white' }
  // No "Pickup at Store" action — switching a delivery order to in-store
  // pickup isn't done from this panel.

  switch (status) {
    case DELIVERY_STATUS.OUT_FOR_DELIVERY:
      return [failed]
    case DELIVERY_STATUS.DELIVERY_FAILED:
      return [outAgain]
    case DELIVERY_STATUS.DELIVERED:
    case DELIVERY_STATUS.PICKUP_AT_STORE:
      return [] // terminal — no further action
    default: // none / preparing
      return [out]
  }
}

// Delivery-order section of the receipt modal: current fulfillment state,
// the contextual actions to advance it, and the change timeline. Renders
// only for delivery orders, which is what makes this modal the "delivery
// version" of the receipt detail.
function DeliveryStatusPanel({
  receiptId,
  currentStatus,
  readOnly = false,
}: {
  receiptId: number
  currentStatus: string
  // Status pill + timeline stay (pure reads); only the transition buttons
  // go. Used for receipts rung at another store.
  readOnly?: boolean
}) {
  const showToast = useUIStore((s) => s.showToast)
  const setDeliveryStatus = useSetDeliveryStatus()
  const { data: history, isLoading } = useDeliveryStatusHistory(receiptId)

  // The latest log entry is the source of truth for the current state; the
  // receipt-payload value is only the initial hint before history loads.
  const latest = history && history.length > 0 ? history[history.length - 1] : null
  const status = latest?.status ?? currentStatus ?? DELIVERY_STATUS.NONE
  const actions = modalDeliveryActions(status)

  const fmtWhen = (iso: string | null): string => {
    if (!iso) return ''
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString()
  }

  const apply = (next: DeliveryStatus) => {
    setDeliveryStatus.mutate(
      { receiptId, status: next },
      {
        onSuccess: () =>
          showToast({
            type: 'success',
            title: 'Delivery updated',
            message: DELIVERY_STATUS_LABEL[next] ?? next,
          }),
        onError: (err) =>
          showToast({
            type: 'error',
            title: 'Could not update delivery',
            message: err instanceof Error ? err.message : undefined,
          }),
      },
    )
  }

  return (
    <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50/60 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Truck size={16} className="text-gray-500" />
          <span className="text-sm font-semibold text-gray-900">Delivery</span>
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${deliveryStatusTint(status)}`}>
            {DELIVERY_STATUS_LABEL[status] ?? status}
          </span>
        </div>
      </div>

      {/* Actions */}
      {!readOnly && actions.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {actions.map((a) => (
            <button
              key={a.label}
              disabled={setDeliveryStatus.isPending}
              onClick={() => apply(a.status)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors disabled:opacity-50 cursor-pointer ${a.tone}`}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}

      {/* Timeline */}
      <div className="mt-4">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          <Clock size={12} /> Timeline
        </div>
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
            <Loader2 size={12} className="animate-spin" /> Loading history…
          </div>
        ) : !history || history.length === 0 ? (
          <p className="text-xs text-gray-400 py-1">No status changes yet.</p>
        ) : (
          <ol className="space-y-2">
            {history.map((e, i) => {
              const isLast = i === history.length - 1
              return (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-1 shrink-0">
                    {e.status === DELIVERY_STATUS.DELIVERED ? (
                      <CheckCircle2 size={14} className="text-green-600" />
                    ) : (
                      <span
                        className={`block w-2.5 h-2.5 rounded-full ${
                          isLast ? 'bg-blue-500 ring-2 ring-blue-100' : 'bg-gray-300'
                        }`}
                      />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-gray-800">
                        {DELIVERY_STATUS_LABEL[e.status] ?? e.status}
                      </span>
                      <span className="text-xs text-gray-400 tabular-nums shrink-0">
                        {fmtWhen(e.changed_at)}
                      </span>
                    </div>
                    {(e.clerk_name || e.note) && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {[e.clerk_name, e.note].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
        )}
      </div>
    </div>
  )
}
